/**
 * Video Constraints Utility
 *
 * Centralized module for video resolution presets and constraints logic.
 * Used by RoomClient (MediaSoup), useMediaSettings, and settings UI flows.
 *
 * NOTE: These constraints are applied to Meet mode only (not Webinar).
 */

export type TVideoResolutionPreset = '4k' | '1440p' | 'high' | 'medium' | 'low' | 'veryLow';

export interface IVideoResolutionOption {
  value: TVideoResolutionPreset;
  label: string;
  width: number;
  height: number;
  description?: string;
}

/**
 * Video resolution presets ordered from highest to lowest quality.
 * These are used both for UI selection and for fallback logic.
 */
export const VIDEO_RESOLUTION_PRESETS: IVideoResolutionOption[] = [
  {
    value: '4k',
    label: '4K (Ultra HD)',
    width: 3840,
    height: 2160,
    description: 'Maximum quality, requires high bandwidth',
  },
  {
    value: '1440p',
    label: '1440p (2K/QHD)',
    width: 2560,
    height: 1440,
    description: 'Very high quality, high bandwidth',
  },
  {
    value: 'high',
    label: '1080p (Full HD)',
    width: 1920,
    height: 1080,
    description: 'High quality, moderate bandwidth',
  },
  {
    value: 'medium',
    label: '720p (HD)',
    width: 1280,
    height: 720,
    description: 'Recommended for most connections',
  },
  {
    value: 'low',
    label: '480p (SD)',
    width: 854,
    height: 480,
    description: 'Lower bandwidth usage',
  },
  {
    value: 'veryLow',
    label: '360p (Low)',
    width: 640,
    height: 360,
    description: 'Minimum bandwidth, reduced quality',
  },
];

/**
 * Default video resolution preset
 */
export const DEFAULT_VIDEO_RESOLUTION: TVideoResolutionPreset = 'high';

/**
 * Get a resolution preset by its value/name
 */
export const getResolutionPreset = (presetName: TVideoResolutionPreset): IVideoResolutionOption | undefined => {
  return VIDEO_RESOLUTION_PRESETS.find((p) => p.value === presetName);
};

/**
 * Reorder presets to start with the user's preferred quality.
 * This is used for fallback logic - we try the preferred quality first,
 * then fall back to other qualities if the device can't support it.
 */
export const getReorderedPresets = (preferredQuality: TVideoResolutionPreset): IVideoResolutionOption[] => {
  const preferredPreset = getResolutionPreset(preferredQuality);
  if (!preferredPreset) {
    return [...VIDEO_RESOLUTION_PRESETS];
  }

  const reorderedPresets: IVideoResolutionOption[] = [preferredPreset];

  VIDEO_RESOLUTION_PRESETS.forEach((preset) => {
    if (preset.value !== preferredQuality) {
      reorderedPresets.push(preset);
    }
  });

  return reorderedPresets;
};

/**
 * Detect if the current device is mobile
 */
export const isMobileDevice = (): boolean => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

/**
 * Determine if a deviceId represents a front-facing camera on mobile
 */
export const isFrontCamera = (deviceId?: string): boolean => {
  if (!deviceId) return true;
  return (
    deviceId === 'default' ||
    deviceId === 'mobile-front' ||
    deviceId.includes('front') ||
    deviceId.includes('user') ||
    deviceId.includes('FaceTime')
  );
};

/**
 * Determine if a deviceId represents a back-facing camera on mobile
 */
export const isBackCamera = (deviceId?: string): boolean => {
  if (!deviceId) return false;
  return deviceId === 'mobile-back' || deviceId.includes('back') || deviceId.includes('environment');
};

/**
 * Build video constraints for getUserMedia based on resolution preset and device.
 *
 * @param preset - The resolution preset to use
 * @param deviceId - Optional device ID for camera selection
 * @param options - Additional options
 * @returns MediaStreamConstraints object
 */
export const buildVideoConstraints = (
  preset: IVideoResolutionOption,
  deviceId?: string,
  options?: {
    useExactDeviceId?: boolean;
    frameRate?: number;
  }
): MediaStreamConstraints => {
  const frameRate = options?.frameRate ?? 30;
  const isMobile = isMobileDevice();

  const constraints: MediaStreamConstraints = {
    audio: false,
    video: {
      width: { ideal: preset.width },
      height: { ideal: preset.height },
      aspectRatio: 1.777, // 16:9 aspect ratio
      frameRate: { ideal: frameRate },
    },
  };

  // Handle device selection for mobile vs desktop
  if (isMobile) {
    // For mobile devices, use facingMode for camera selection
    if (isBackCamera(deviceId)) {
      (constraints.video as MediaTrackConstraints).facingMode = 'environment';
    } else {
      // Default to front camera for mobile
      (constraints.video as MediaTrackConstraints).facingMode = 'user';
    }
  } else if (deviceId && deviceId !== 'default') {
    // For desktop devices, use deviceId when specified
    if (options?.useExactDeviceId !== false) {
      (constraints.video as MediaTrackConstraints).deviceId = { exact: deviceId };
    } else {
      (constraints.video as MediaTrackConstraints).deviceId = deviceId;
    }
  }

  return constraints;
};

/**
 * Build fallback video constraints without exact device requirement.
 * Used when the primary constraints fail due to OverconstrainedError.
 */
export const buildFallbackConstraints = (preset: IVideoResolutionOption, deviceId?: string): MediaStreamConstraints => {
  const isMobile = isMobileDevice();

  const constraints: MediaStreamConstraints = {
    audio: false,
    video: {
      width: { ideal: preset.width },
      height: { ideal: preset.height },
      aspectRatio: 1.777,
      frameRate: { ideal: 30 },
    },
  };

  // For desktop, try non-exact deviceId
  if (!isMobile && deviceId && deviceId !== 'default') {
    (constraints.video as MediaTrackConstraints).deviceId = deviceId;
  }

  return constraints;
};

/**
 * Attempt to get a camera stream with quality fallback.
 * Tries the preferred quality first, then falls back to lower qualities if needed.
 *
 * @param deviceId - Camera device ID
 * @param preferredQuality - Preferred resolution preset
 * @returns Promise resolving to MediaStream
 */
export const getUserMediaWithFallback = async (
  deviceId?: string,
  preferredQuality: TVideoResolutionPreset = DEFAULT_VIDEO_RESOLUTION
): Promise<MediaStream> => {
  const reorderedPresets = getReorderedPresets(preferredQuality);
  const isMobile = isMobileDevice();

  for (const preset of reorderedPresets) {
    try {
      const constraints = buildVideoConstraints(preset, deviceId, { useExactDeviceId: true });
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (error: unknown) {
      const err = error as Error & { name?: string };

      // For mobile devices, try alternative approach if facingMode fails
      if (isMobile && err.name === 'OverconstrainedError') {
        try {
          const fallbackConstraints = buildFallbackConstraints(preset, deviceId);
          const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          return stream;
        } catch (fallbackError) {
          console.warn('Mobile fallback attempt also failed:', (fallbackError as Error).message);
        }
      }

      // For desktop devices, try without exact constraint as fallback
      if (!isMobile && err.name === 'OverconstrainedError' && deviceId && deviceId !== 'default') {
        try {
          const fallbackConstraints = buildFallbackConstraints(preset, deviceId);
          const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          return stream;
        } catch (fallbackError) {
          console.warn('Desktop fallback attempt also failed:', (fallbackError as Error).message);
        }
      }

      // If it's the last preset, throw the error
      if (preset === reorderedPresets[reorderedPresets.length - 1]) {
        throw error;
      }
    }
  }

  // This should never be reached, but TypeScript needs it
  throw new Error('Failed to get camera stream with any quality preset');
};

/**
 * Get the base video constraints for MediaSoup production.
 * Used by RoomClient.getVideoConstraints()
 *
 * @param deviceId - Camera device ID
 * @param resolution - Resolution preset to use
 * @returns MediaStreamConstraints object
 */
export const getMediaSoupVideoConstraints = (
  deviceId?: string,
  resolution: TVideoResolutionPreset = DEFAULT_VIDEO_RESOLUTION
): MediaStreamConstraints => {
  const preset = getResolutionPreset(resolution) ?? getResolutionPreset(DEFAULT_VIDEO_RESOLUTION)!;
  return buildVideoConstraints(preset, deviceId, { useExactDeviceId: true });
};
